import React, { memo } from 'react';
import {
  Box,
  MenuItem,
  Select,
  FormControl,
  Paper,
  Checkbox,
  ListItemText,
} from '@mui/material';
import { DateRangePicker } from '@/shared/ui/DateRangePicker';
import type { FilterBarProps } from '@/entities/campaign';
import { RootState, useAppSelector } from '@/app/store';

const FilterBar: React.FC<FilterBarProps> = memo(
  ({
    formik,
    screenSizeRange,
    setScreenSizeRange,
    screenType,
    setScreenType,
    screenSizeBounds = { min: 10, max: 800, availableSizes: [] },
    availableScreenTypes = [],
    zone,
    setZone,
    propertyType,
    setPropertyType,
    availableZones = [],
    availablePropertyTypes = [],
  }) => {
    const { campaignStartDate, campaignEndDate } = useAppSelector(
      (state: RootState) => state.dooh.campaigns.campaignDetails
    );

    return (
      <Paper elevation={2} sx={{ p: 2, mb: 2 }}>
        <Box
          sx={{
            display: 'flex',
            alignItems: { xs: 'stretch', sm: 'center' },
            flexWrap: 'wrap',
            columnGap: 2,
            rowGap: 2.5,
          }}
        >
          {setScreenType && (
            <FormControl
              sx={{
                m: 0,
                minWidth: { xs: '100%', sm: 150 },
                flex: '1 1 180px',
              }}
              size="small"
              fullWidth
            >
              <Select
                variant="outlined"
                value={screenType || ''}
                onChange={e => setScreenType?.(e.target.value)}
                displayEmpty
                fullWidth
              >
                <MenuItem value="">All Screen Types</MenuItem>
                {availableScreenTypes.map(type => (
                  <MenuItem key={type} value={type}>
                    {type}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          )}

          {setZone && availableZones.length > 0 && (
            <FormControl
              sx={{
                m: 0,
                minWidth: { xs: '100%', sm: 150 },
                flex: '1 1 180px',
              }}
              size="small"
              fullWidth
            >
              <Select
                variant="outlined"
                value={zone || ''}
                onChange={e => setZone?.(e.target.value)}
                displayEmpty
                fullWidth
              >
                <MenuItem value="">All Zones</MenuItem>
                {availableZones.map(z => (
                  <MenuItem key={z} value={z}>
                    {z}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          )}

          {setPropertyType && availablePropertyTypes.length > 0 && (
            <FormControl
              sx={{
                m: 0,
                minWidth: { xs: '100%', sm: 150 },
                flex: '1 1 180px',
              }}
              size="small"
              fullWidth
            >
              <Select
                variant="outlined"
                value={propertyType || ''}
                onChange={e => setPropertyType?.(e.target.value)}
                displayEmpty
                fullWidth
              >
                <MenuItem value="">All Property Types</MenuItem>
                {availablePropertyTypes.map(pt => (
                  <MenuItem key={pt} value={pt}>
                    {pt}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          )}

          {/* Screen Size Multi-Select Filter */}
          {setScreenSizeRange && screenSizeBounds.availableSizes.length > 0 && (
            <FormControl
              sx={{
                m: 0,
                minWidth: { xs: '100%', sm: 150 },
                flex: '1 1 180px',
              }}
              size="small"
              fullWidth
            >
              <Select
                multiple
                variant="outlined"
                value={screenSizeRange ?? []}
                onChange={e => setScreenSizeRange?.(e.target.value as number[])}
                displayEmpty
                fullWidth
                renderValue={selected => {
                  const sizes = selected as number[];
                  if (!sizes || sizes.length === 0)
                    return (
                      <em style={{ color: 'inherit', fontStyle: 'normal' }}>
                        All Screen Sizes
                      </em>
                    );
                  return sizes.map(s => `${s}"`).join(', ');
                }}
              >
                {screenSizeBounds.availableSizes.map(size => (
                  <MenuItem key={size} value={size}>
                    <Checkbox
                      checked={(screenSizeRange ?? []).includes(size)}
                      size="small"
                    />
                    <ListItemText primary={`${size}"`} />
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          )}

          <DateRangePicker
            startDate={campaignStartDate}
            endDate={campaignEndDate}
            onDateRangeChange={(startDate, endDate) => {
              formik.setField('campaignStartDate', startDate);
              formik.setField('campaignEndDate', endDate);
            }}
            label="Campaign Date Range"
            disabled={true}
            size="small"
            fullWidth
            sx={{ m: 0, minWidth: { xs: '100%', sm: 280 }, flex: '1 1 280px' }}
          />
        </Box>
      </Paper>
    );
  }
);

FilterBar.displayName = 'FilterBar';

export default FilterBar;
